export const GOOGLE_DRIVE_SCOPE = "https://www.googleapis.com/auth/drive.file";
export const GOOGLE_DRIVE_FOLDER = "Runner Forge Backups";
export const GOOGLE_DRIVE_AUTO_BACKUP = "Runner Forge Automatic Backup.json";
export const GOOGLE_IDENTITY_SCRIPT = "https://accounts.google.com/gsi/client";
export const MAX_GOOGLE_DRIVE_BACKUP_BYTES = 5 * 1024 * 1024;

const DRIVE_API = "https://www.googleapis.com/drive/v3";
const DRIVE_UPLOAD_API = "https://www.googleapis.com/upload/drive/v3";
const FOLDER_MIME = "application/vnd.google-apps.folder";
const BACKUP_MIME = "application/json";
const FOLDER_MARKER = "runner-forge-backup-folder";
const BACKUP_MARKER = "runner-forge-library-backup";

let identityPromise;

export const googleDriveEnvironmentAvailable = (clientId, locationRef = globalThis.location) => {
  if (!String(clientId || "").trim()) return false;
  if (!locationRef) return false;
  return locationRef.protocol === "https:" || ["localhost", "127.0.0.1"].includes(locationRef.hostname);
};

export const loadGoogleIdentity = ({ documentRef = globalThis.document, googleProvider = () => globalThis.google } = {}) => {
  if (googleProvider()?.accounts?.oauth2) return Promise.resolve();
  if (identityPromise) return identityPromise;
  if (!documentRef?.head) return Promise.reject(new Error("Google authorization requires a browser document."));

  identityPromise = new Promise((resolve, reject) => {
    const existing = documentRef.querySelector(`script[src="${GOOGLE_IDENTITY_SCRIPT}"]`);
    const script = existing || documentRef.createElement("script");
    const loaded = () => googleProvider()?.accounts?.oauth2
      ? resolve()
      : reject(new Error("Google authorization loaded without its OAuth interface."));
    script.addEventListener("load", loaded, { once: true });
    script.addEventListener("error", () => reject(new Error("Google authorization could not be loaded.")), { once: true });
    if (!existing) {
      script.src = GOOGLE_IDENTITY_SCRIPT;
      script.async = true;
      script.defer = true;
      script.referrerPolicy = "origin";
      script.dataset.runnerForgeGoogleIdentity = "true";
      documentRef.head.appendChild(script);
    }
  }).catch((error) => {
    identityPromise = undefined;
    throw error;
  });
  return identityPromise;
};

const queryValue = (value) => String(value).replaceAll("\\", "\\\\").replaceAll("'", "\\'");

export class GoogleDriveBackupClient {
  constructor({
    clientId,
    fetchImpl = globalThis.fetch?.bind(globalThis),
    identityLoader = loadGoogleIdentity,
    googleProvider = () => globalThis.google,
    now = () => Date.now(),
  } = {}) {
    this.clientId = String(clientId || "").trim();
    this.fetchImpl = fetchImpl;
    this.identityLoader = identityLoader;
    this.googleProvider = googleProvider;
    this.now = now;
    this.accessToken = "";
    this.expiresAt = 0;
    this.folderId = "";
    this.autoBackupId = "";
    this.tokenClient = null;
  }

  get connected() {
    return Boolean(this.accessToken) && this.expiresAt > this.now() + 30_000;
  }

  async connect() {
    if (!this.clientId) throw new Error("Google Drive is not configured for this deployment.");
    if (!this.fetchImpl) throw new Error("This browser cannot contact Google Drive.");
    await this.identityLoader();
    const oauth = this.googleProvider()?.accounts?.oauth2;
    if (!oauth?.initTokenClient) throw new Error("Google authorization is unavailable.");

    if (!this.tokenClient) {
      this.tokenClient = oauth.initTokenClient({ client_id: this.clientId, scope: GOOGLE_DRIVE_SCOPE, callback: () => {} });
    }

    const response = await new Promise((resolve, reject) => {
      this.tokenClient.callback = (tokenResponse) => {
        if (tokenResponse?.error) reject(new Error(tokenResponse.error_description || tokenResponse.error));
        else resolve(tokenResponse);
      };
      this.tokenClient.error_callback = (error) => reject(new Error(error?.message || error?.type || "Google authorization was cancelled."));
      this.tokenClient.requestAccessToken();
    });

    this.accessToken = response.access_token;
    this.expiresAt = this.now() + Math.max(0, Number(response.expires_in) || 0) * 1000;
    return { expiresAt: this.expiresAt, scope: response.scope || GOOGLE_DRIVE_SCOPE };
  }

  async disconnect() {
    const token = this.accessToken;
    this.accessToken = "";
    this.expiresAt = 0;
    this.folderId = "";
    this.autoBackupId = "";
    const revoke = this.googleProvider()?.accounts?.oauth2?.revoke;
    if (!token || !revoke) return;
    await new Promise((resolve) => {
      const timeout = globalThis.setTimeout(resolve, 3000);
      revoke(token, () => { globalThis.clearTimeout(timeout); resolve(); });
    });
  }

  async request(url, options = {}) {
    if (!this.connected) throw new Error("Google Drive authorization has expired. Connect again to continue.");
    const { responseType, ...fetchOptions } = options;
    const headers = new Headers(fetchOptions.headers || {});
    headers.set("Authorization", `Bearer ${this.accessToken}`);
    const response = await this.fetchImpl(url, { ...fetchOptions, headers });
    const text = await response.text();
    if (!response.ok) {
      if (response.status === 401) { this.accessToken = ""; this.expiresAt = 0; }
      let detail = "";
      try { detail = JSON.parse(text)?.error?.message || ""; } catch {}
      throw new Error(detail || `Google Drive request failed (${response.status}).`);
    }
    if (!text) return null;
    if (responseType === "text") return text;
    const contentType = response.headers.get("content-type") || "";
    return contentType.includes("application/json") ? JSON.parse(text) : text;
  }

  async ensureFolder() {
    if (this.folderId) return this.folderId;
    const query = [
      `mimeType='${FOLDER_MIME}'`,
      "trashed=false",
      `appProperties has { key='runnerForgeType' and value='${FOLDER_MARKER}' }`,
    ].join(" and ");
    const params = new URLSearchParams({ q: query, spaces: "drive", pageSize: "10", fields: "files(id,name)" });
    const listing = await this.request(`${DRIVE_API}/files?${params}`);
    const existing = listing.files?.find((file) => file.name === GOOGLE_DRIVE_FOLDER) || listing.files?.[0];
    if (existing) { this.folderId = existing.id; return existing.id; }

    const folder = await this.request(`${DRIVE_API}/files?fields=id,name`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: GOOGLE_DRIVE_FOLDER,
        mimeType: FOLDER_MIME,
        appProperties: { runnerForgeType: FOLDER_MARKER },
      }),
    });
    this.folderId = folder.id;
    return folder.id;
  }

  async writeLibraryBackup({ backup, filename, appVersion, fileId = "", mode = "manual" }) {
    const backupJson = JSON.stringify(backup, null, 2);
    if (new TextEncoder().encode(backupJson).byteLength > MAX_GOOGLE_DRIVE_BACKUP_BYTES) {
      throw new Error("Google Drive backups are limited to 5 MB. Download the full library JSON instead.");
    }
    const folderId = await this.ensureFolder();
    const boundary = `runner_forge_${globalThis.crypto?.randomUUID?.() || Math.random().toString(16).slice(2)}`;
    const metadata = {
      name: filename,
      mimeType: BACKUP_MIME,
      description: mode === "automatic" ? `Runner Forge v${appVersion} rolling automatic library backup` : `Runner Forge v${appVersion} full library backup`,
      appProperties: {
        runnerForgeType: BACKUP_MARKER,
        runnerForgeVersion: String(appVersion),
        backupVersion: String(backup.backupVersion),
        runnerForgeBackupMode: mode,
      },
    };
    if (!fileId) metadata.parents = [folderId];
    const body = [
      `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${JSON.stringify(metadata)}\r\n`,
      `--${boundary}\r\nContent-Type: application/json\r\n\r\n${backupJson}\r\n`,
      `--${boundary}--`,
    ].join("");
    return this.request(`${DRIVE_UPLOAD_API}/files${fileId ? `/${encodeURIComponent(fileId)}` : ""}?uploadType=multipart&fields=id,name,createdTime,modifiedTime,size,webViewLink,appProperties`, {
      method: fileId ? "PATCH" : "POST",
      headers: { "Content-Type": `multipart/related; boundary=${boundary}` },
      body,
    });
  }

  async uploadLibraryBackup({ backup, filename, appVersion }) {
    return this.writeLibraryBackup({ backup, filename, appVersion, mode: "manual" });
  }

  async upsertAutomaticBackup({ backup, appVersion }) {
    if (!this.autoBackupId) await this.listLibraryBackups();
    const uploaded = await this.writeLibraryBackup({
      backup,
      filename: GOOGLE_DRIVE_AUTO_BACKUP,
      appVersion,
      fileId: this.autoBackupId,
      mode: "automatic",
    });
    this.autoBackupId = uploaded.id;
    return uploaded;
  }

  async listLibraryBackups() {
    const folderId = await this.ensureFolder();
    const query = [
      `'${queryValue(folderId)}' in parents`,
      "trashed=false",
      `appProperties has { key='runnerForgeType' and value='${BACKUP_MARKER}' }`,
    ].join(" and ");
    const params = new URLSearchParams({
      q: query,
      spaces: "drive",
      pageSize: "100",
      orderBy: "modifiedTime desc",
      fields: "files(id,name,createdTime,modifiedTime,size,webViewLink,appProperties)",
    });
    const listing = await this.request(`${DRIVE_API}/files?${params}`);
    const files = Array.isArray(listing.files) ? listing.files : [];
    this.autoBackupId = files.find((file) => file.appProperties?.runnerForgeBackupMode === "automatic")?.id || "";
    return files;
  }

  async downloadLibraryBackup(fileId) {
    const text = await this.request(`${DRIVE_API}/files/${encodeURIComponent(fileId)}?alt=media`, { responseType: "text" });
    try { return JSON.parse(text); }
    catch { throw new Error("That Google Drive backup is not valid JSON."); }
  }

  folderUrl() {
    return this.folderId ? `https://drive.google.com/drive/folders/${encodeURIComponent(this.folderId)}` : "";
  }
}
